import { StarsLayer as SL } from '@fe/maplibre-extensions';
import { CustomLayerInterface } from 'maplibre-gl';
import React, { useEffect } from 'react';
import { StarmapType } from '../../../../common/enums';
import { createStarmapProvider } from '../../../../services/starmap-loader';
import { useAppSelector } from '../../../hooks/store-hooks';
import useMap from '../../../hooks/use-map';
import { useWorkspaceView } from '../hooks/use-view';

const LAYER_ID = 'fe-stars-layer';
const LAYER_SLOT = 'fe-stars-slot';
const SIDEREAL_UPDATE_MS = 10 * 1000;

const CUBEMAP_URLS: Record<StarmapType, string> = {
    [StarmapType.basic]: '/img/starmaps/basic-etc1s.ktx2',
    [StarmapType.enhanced]: '/img/starmaps/enhanced-etc1s.ktx2'
};

export default function StarsLayer() {
    const map = useMap();
    const [instance, setInstance] = React.useState<SL | null>(null);

    const show = useAppSelector((x) => x.settings.hasStars);
    const starmapType = useAppSelector((x) => x.settings.starmapType);
    const globe = useWorkspaceView((x) => x.map.useGlobeView);

    const provider = React.useMemo(() => createStarmapProvider(CUBEMAP_URLS[starmapType]), [starmapType]);

    useEffect(() => {
        if (map && show && globe && !instance) {
            if (!map.getLayer(LAYER_ID)) {
                const sl = new SL(LAYER_ID, provider);
                setInstance(sl);
                map.addLayer(sl as CustomLayerInterface, LAYER_SLOT);
                map.setLayoutProperty(LAYER_ID, 'visibility', show ? 'visible' : 'none');
            }
        }

        return () => {
            if (map && map.getLayer(LAYER_ID)) {
                map.removeLayer(LAYER_ID);
                setInstance(null);
            }
        };
    }, [map, show, globe]);

    useEffect(() => {
        if (map && map.getLayer(LAYER_ID)) {
            map.setLayoutProperty(LAYER_ID, 'visibility', show ? 'visible' : 'none');
        }
    }, [map, show]);

    React.useEffect(() => {
        if (!instance) {
            return;
        }
        instance.setProvider(provider);
    }, [instance, provider]);

    if (map && instance && map.getLayer(LAYER_ID)) {
        if (show && globe) {
            map._canvas.style.backgroundColor = 'black';
            map.setLayoutProperty(LAYER_ID, 'visibility', 'visible');
        } else {
            map._canvas.style.backgroundColor = '';
            map.setLayoutProperty(LAYER_ID, 'visibility', 'none');
        }
    }

    React.useEffect(() => {
        if (!instance || !show || !globe) {
            return;
        }
        instance.setTime(new Date());
        const id = self.setInterval(() => instance.setTime(new Date()), SIDEREAL_UPDATE_MS);
        return () => self.clearInterval(id);
    }, [instance, show, globe]);

    return null;
}
